import { confirmBox } from '../components/dialog.js';
import { openExercisePicker } from '../components/exercise-picker.js';
import { renderHeader } from '../components/header.js';
import {
  activeProgram, loadProgramDays, loadDayExercises, updateProgramDay, addProgramDay, deleteProgramDay,
  addProgramExercise, updateProgramExercise, deleteProgramExercise, reorderProgramExercises,
  loadExerciseLibrary, createExercise, loadSettings,
} from '../lib/grit-data.js';
import { activeUser, isViewingSelf } from '../lib/auth.js';
import { displayName, displayNameHtml, ensureNames } from '../lib/exercise-names.js';
import { go } from '../router.js';
import { toast } from '../components/toast.js';
import { icon } from '../components/icons.js';

const WD = ['', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const KINDS = [['lift', 'Lift'], ['run', 'Run'], ['hiit', 'HIIT'], ['rest', 'Rest']];

const ICON = {
  up: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4"><polyline points="18 15 12 9 6 15"/></svg>',
  down: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4"><polyline points="6 9 12 15 18 9"/></svg>',
  close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>',
  plus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>',
};

export function renderProgramEditor(el) {
  if (!isViewingSelf()) { go('settings'); return; }

  let program = null;
  let days = [];        // [{ ...day, exercises: [pe...] }]
  let selectedId = null;
  let expandedId = null;
  let restDefault = 180;

  el.innerHTML = `${renderHeader()}<div class="grit-page px-5 pt-5"><p class="text-inksoft">Loading…</p></div>`;

  async function reload() {
    await ensureNames(activeUser());
    program = await activeProgram(activeUser());
    if (!program) { days = []; paint(); return; }
    const ds = await loadProgramDays(activeUser());
    days = await Promise.all(ds.map(async (d) => ({ ...d, exercises: await loadDayExercises(d.id) })));
    if (!selectedId || !days.find((d) => d.id === selectedId)) selectedId = days[0]?.id || null;
    paint();
  }

  (async () => {
    restDefault = (await loadSettings(activeUser())).rest_default || 180;
    reload();
  })();

  function dayPills() {
    return `<div class="flex gap-2 overflow-x-auto pb-1 -mx-5 px-5 mt-3" style="scrollbar-width:none">
      ${days.map((d) => `<button data-day="${d.id}" class=\"flex-none grit-pill ${d.id === selectedId ? '!bg-ink !text-white' : ''}\">${WD[d.weekday] || '?'} · ${d.name}</button>`).join('')}
      <button data-addday class="flex-none grit-pill !bg-card !text-grit">${ICON.plus} Add day</button>
    </div>`;
  }

  function exField(pe, key, val, label, w = 'w-14') {
    return `<label class="flex flex-col items-center gap-1">
      <input data-pe="${pe.id}" data-field="${key}" type="number" inputmode="numeric" value="${val ?? ''}" aria-label="${label}" class="grit-input ${w} text-center !px-1 font-semibold" />
      <span class="text-[13px] text-inksoft">${label}</span>
    </label>`;
  }

  function exerciseCard(pe, i, n) {
    const open = pe.id === expandedId;
    const summary = `${pe.target_sets}×${pe.rep_low}-${pe.rep_high} · rest ${Math.round((pe.rest_seconds || 0) / 60)}m · +${pe.increment_lb}`;
    return `<div class="grit-card px-2 py-1 mb-2">
      <div class="flex items-center gap-1">
        <div class="flex flex-col">
          <button data-mv="up" data-pe="${pe.id}" ${i === 0 ? 'disabled' : ''} aria-label="Move up" class="w-11 h-11 rounded-full flex items-center justify-center text-inksoft active:bg-fill disabled:opacity-20">${ICON.up}</button>
          <button data-mv="down" data-pe="${pe.id}" ${i === n - 1 ? 'disabled' : ''} aria-label="Move down" class="w-11 h-11 rounded-full flex items-center justify-center text-inksoft active:bg-fill disabled:opacity-20">${ICON.down}</button>
        </div>
        <button data-expand="${pe.id}" class="flex-1 min-w-0 text-left min-h-[56px]">
          <div class="font-semibold text-[17px] text-ink truncate">${displayNameHtml(pe.exercise) || 'Exercise'}</div>
          <div class="text-[13px] text-inksoft">${summary}${pe.knee ? ' · knee check' : ''}</div>
        </button>
        <button data-rmex="${pe.id}" aria-label="Remove" class="flex-none w-11 h-11 -mr-1 flex items-center justify-center text-inksoft">${ICON.close}</button>
      </div>
      ${open ? `<div class="mt-3 pt-3 border-t border-hairline grid grid-cols-5 gap-2">
        ${exField(pe, 'target_sets', pe.target_sets, 'Sets', 'w-full')}
        ${exField(pe, 'rep_low', pe.rep_low, 'Min', 'w-full')}
        ${exField(pe, 'rep_high', pe.rep_high, 'Max', 'w-full')}
        ${exField(pe, 'increment_lb', pe.increment_lb, '+lb', 'w-full')}
        ${exField(pe, 'start_weight_lb', pe.start_weight_lb, 'Start', 'w-full')}
      </div>
      <div class="mt-3"><label class="flex items-center justify-between"><span class="text-[15px] text-ink">Rest (seconds)</span>
        <input data-pe="${pe.id}" data-field="rest_seconds" type="number" inputmode="numeric" value="${pe.rest_seconds ?? ''}" class="grit-input !w-24 text-center font-semibold" /></label></div>` : ''}
    </div>`;
  }

  function paint() {
    if (!program) {
      el.innerHTML = `${renderHeader()}<div class="grit-page px-5 pt-5">
        <button id="back" class="text-grit text-[17px] min-h-[44px] -ml-1 mb-1 inline-flex items-center gap-1">${icon.left('w-5 h-5')} Settings</button>
        <div class="grit-card p-6 text-center"><div class="font-bold text-ink">No program yet</div>
        <div class="text-[15px] text-inksoft mt-1">Set up a program from the Today tab first.</div></div></div>`;
      document.getElementById('back').onclick = () => go('settings');
      return;
    }
    const day = days.find((d) => d.id === selectedId);
    const body = !day ? '' : (day.kind === 'lift'
      ? `${day.exercises.map((pe, i) => exerciseCard(pe, i, day.exercises.length)).join('') || '<div class="text-[15px] text-inksoft text-center py-4">No exercises yet.</div>'}
         <button id="addex" class="grit-btn-quiet mt-2 !text-grit">${ICON.plus} Add exercise</button>`
      : `<div class="grit-card p-5 text-center text-[15px] text-inksoft">${day.kind === 'run' ? 'Run day. Directions come from your run plan.' : day.kind === 'hiit' ? 'HIIT day. Follow your video and check it off.' : 'Rest day.'}</div>`);

    el.innerHTML = `
      ${renderHeader()}
      <div class="grit-page px-5 pt-5">
        <button id="back" class="text-grit text-[17px] min-h-[44px] -ml-1 inline-flex items-center gap-1">${icon.left('w-5 h-5')} Settings</button>
        <div class="grit-eyebrow">${program.name}</div>
        <h1 class="grit-title mt-1">Edit program</h1>
        ${dayPills()}
        ${day ? `<div class="mt-4 mb-3 space-y-2">
          <input id="dayname" value="${day.name}" class="grit-input font-semibold" />
          <div class="flex items-center gap-2">
            <select id="dayweekday" class="grit-input flex-1 min-w-0 font-semibold">${WD.map((w, idx) => idx ? `<option value="${idx}" ${day.weekday === idx ? 'selected' : ''}>${w}</option>` : '').join('')}</select>
            <select id="daykind" class="grit-input flex-1 min-w-0 font-semibold">${KINDS.map(([k, l]) => `<option value="${k}" ${day.kind === k ? 'selected' : ''}>${l}</option>`).join('')}</select>
            <button id="delday" class="grit-btn-danger !w-auto !min-h-[48px] px-4 !text-[15px] flex-none">Delete</button>
          </div>
        </div>` : ''}
        ${body}
        <div class="h-24"></div>
      </div>`;

    document.getElementById('back').onclick = () => go('settings');
    el.querySelectorAll('[data-day]').forEach((b) => { b.onclick = () => { selectedId = b.dataset.day; expandedId = null; paint(); }; });
    const ad = document.querySelector('[data-addday]'); if (ad) ad.onclick = () => handleAddDay();
    if (!day) return;

    document.getElementById('dayname').onchange = (e) => updateProgramDay(day.id, { name: e.target.value }).then(() => { day.name = e.target.value; toast('Saved'); paint(); });
    document.getElementById('daykind').onchange = (e) => updateProgramDay(day.id, { kind: e.target.value }).then(() => reload());
    document.getElementById('dayweekday').onchange = (e) => updateProgramDay(day.id, { weekday: Number(e.target.value) }).then(() => { day.weekday = Number(e.target.value); toast('Saved'); paint(); });
    document.getElementById('delday').onclick = () => handleDeleteDay(day);

    el.querySelectorAll('[data-expand]').forEach((b) => { b.onclick = () => { expandedId = expandedId === b.dataset.expand ? null : b.dataset.expand; paint(); }; });
    el.querySelectorAll('[data-rmex]').forEach((b) => { b.onclick = () => handleRemoveEx(b.dataset.rmex); });
    el.querySelectorAll('[data-mv]').forEach((b) => { b.onclick = () => handleMove(b.dataset.pe, b.dataset.mv, day); });
    el.querySelectorAll('input[data-pe]').forEach((inp) => {
      inp.onchange = async () => {
        const val = inp.value === '' ? null : Number(inp.value);
        const ok = await updateProgramExercise(inp.dataset.pe, { [inp.dataset.field]: val });
        if (!ok) toast("Didn't save", { tone: 'bad' });
        else { const pe = day.exercises.find((p) => p.id === inp.dataset.pe); if (pe) pe[inp.dataset.field] = val; toast('Saved'); }
      };
    });
    const ax = document.getElementById('addex'); if (ax) ax.onclick = () => openPicker(day);
  }

  async function handleAddDay() {
    const used = new Set(days.map((d) => d.weekday));
    let wd = 1; while (used.has(wd) && wd < 7) wd++;
    const sort = Math.max(0, ...days.map((d) => d.sort)) + 1;
    const created = await addProgramDay(program.id, { name: 'New day', weekday: wd, kind: 'lift', sort });
    if (!created) { toast("Couldn't add day", { tone: 'bad' }); return; }
    selectedId = created.id; expandedId = null;
    await reload();
  }

  async function handleDeleteDay(day) {
    if (!(await confirmBox({ title: `Delete "${day.name}"?`, message: 'Its exercises go too.', confirmLabel: 'Delete', danger: true }))) return;
    const res = await deleteProgramDay(day.id);
    if (!res.ok) { toast('Has logged history, can\'t delete it', { tone: 'bad' }); return; }
    selectedId = null;
    await reload();
  }

  async function handleRemoveEx(peId) {
    if (!(await confirmBox({ title: 'Remove this exercise from the day?', confirmLabel: 'Remove', danger: true }))) return;
    await deleteProgramExercise(peId);
    await reload();
  }

  async function handleMove(peId, dir, day) {
    const ids = day.exercises.map((p) => p.id);
    const i = ids.indexOf(peId);
    const j = dir === 'up' ? i - 1 : i + 1;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    await reorderProgramExercises(ids);
    await reload();
  }

  // ── Add a lift (shared picker) ──────────────────────────────────────────────
  function openPicker(day) {
    openExercisePicker({
      title: `Add to ${day.name}`,
      usedIds: new Set(day.exercises.map((p) => p.exercise_id)),
      onPick: async (ex, custom) => {
        const sort = Math.max(-1, ...day.exercises.map((p) => p.sort)) + 1;
        const created = await addProgramExercise(day.id, {
          exercise_id: ex.id, sort,
          target_sets: custom?.sets ?? 3, rep_low: custom?.lo ?? ex.default_rep_low ?? 6, rep_high: custom?.hi ?? ex.default_rep_high ?? 8,
          rest_seconds: custom?.rest ?? (ex.is_primary ? 180 : (restDefault < 180 ? restDefault : 120)), increment_lb: custom?.inc ?? ex.default_increment_lb ?? 5, progression_mode: '1',
        });
        if (!created) { toast("Couldn't add", { tone: 'bad' }); return; }
        toast('Added');
        await reload();
      },
    });
  }
}
